import React from 'react';
import { ThemeProvider, CssBaseline, AppBar, Toolbar, Typography, Button, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CloudUpload as CloudUploadIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { createAdminTheme } from '../theme';

/**
 * Fixed admin theme — created once outside the component so it is never
 * recreated on re-renders.  This theme is completely independent of the
 * user-selected palette managed by `useThemeStore`.
 */
const adminTheme = createAdminTheme();

interface AdminLayoutProps {
  children: React.ReactNode;
}

/**
 * AdminLayout — Theme-isolation wrapper for admin pages.
 *
 * Wraps its children in a separate MUI `<ThemeProvider>` that always
 * receives the fixed admin theme produced by `createAdminTheme()`.
 * It does **not** import or read from `useThemeStore`, so admin pages
 * are guaranteed to render with consistent styling regardless of the
 * user's Cyber-Street / Modern-Minimalist preference.
 *
 * Usage:
 *   - As a route-level layout for `/admin/*` routes in App.tsx
 *   - As a direct wrapper around individual admin components
 *     (e.g. `<AdminLayout><AdAnalyticsDashboard /></AdminLayout>`)
 *
 * Requirements: 15.1, 15.2, 15.3
 */
const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <ThemeProvider theme={adminTheme}>
      <CssBaseline />
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <Typography variant="h6" sx={{ mr: 3 }}>
            Admin
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
              size="small"
              color="inherit"
            >
              Back to App
            </Button>
            <Button
              startIcon={<CloudUploadIcon />}
              onClick={() => navigate('/admin/bulk-upload')}
              size="small"
              variant={isActive('/admin/bulk-upload') ? 'contained' : 'text'}
              color={isActive('/admin/bulk-upload') ? 'primary' : 'inherit'}
            >
              Bulk Upload
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ minHeight: '100vh' }}>
        {children}
      </Box>
    </ThemeProvider>
  );
};

export default AdminLayout;
